
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';

const HeroSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    // Staggered animation timing for elements
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/booking${searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : ''}`);
  };

  // Animation variants - shortened for mobile
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: isMobile ? 0.08 : 0.15,
        delayChildren: 0.1
      }
    }
  };
  
  const item = {
    hidden: {
      opacity: 0,
      y: 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: isMobile ? 0.25 : 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  return (
    <section className="relative min-h-[calc(100vh-70px)] md:min-h-screen flex items-center pt-16 md:pt-16 md:py-0 overflow-hidden">
      {/* Optimized background elements with better mobile rendering */}
      <div className="absolute inset-0 bg-pattern opacity-5"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/95 via-[#212121] to-[#212121]"></div>
      <div className="absolute -top-24 -right-24 w-48 md:w-96 h-48 md:h-96 bg-[#2C6E49]/10 rounded-full blur-3xl animate-pulse-subtle"></div>
      <div className="absolute -bottom-32 -left-32 w-48 md:w-96 h-48 md:h-96 bg-[#2C6E49]/5 rounded-full blur-3xl"></div>
      
      <motion.div 
        className="container mx-auto px-4 md:px-8 relative z-10" 
        variants={container} 
        initial="hidden" 
        animate={isVisible ? "show" : "hidden"}
      >
        <div className="max-w-3xl mx-auto text-center">
          {/* Logo with subtle entrance animation - smaller on mobile */}
          <motion.div variants={item} className="mb-6 md:mb-8 flex justify-center">
            <img 
              alt="CHRONA Logo" 
              className="h-16 md:h-28 object-contain drop-shadow-lg animate-float" 
              src="/lovable-uploads/bbbd313b-d6bd-4b33-af58-258cd00a4dc3.png" 
              loading="lazy"
            />
          </motion.div>
          
          <motion.h1 
            variants={item} 
            className="text-2xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-6 leading-tight tracking-tight text-[#F8F8F8]"
          >
            Termin. <span className="text-[#2C6E49]">Einfach buchen.</span>
          </motion.h1>
          
          <motion.p variants={item} className="text-base md:text-xl lg:text-2xl text-[#F8F8F8]/80 mb-6 md:mb-12 px-1">
            Friseur, Nagelstudio oder Coaching – in nur 3 Klicks zum Wunschtermin
          </motion.p>
          
          {/* Search form with mobile-optimized layout */}
          <motion.form 
            variants={item} 
            onSubmit={handleSearch} 
            className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto p-4 backdrop-blur-xl bg-[#191919]/50 border border-[#F8F8F8]/10 rounded-xl md:rounded-[24px] shadow-2xl"
          >
            <Input 
              type="text" 
              placeholder={isMobile ? "Wonach suchst du?" : "Was suchst du? z.B. Friseur in Berlin"} 
              className="flex-1 text-base md:text-lg h-12 md:h-14 rounded-xl md:rounded-[24px] border-0 focus-visible:ring-1 focus-visible:ring-[#2C6E49] focus-visible:ring-offset-0 bg-[#191919]/60 text-[#F8F8F8] placeholder:text-[#F8F8F8]/40 min-h-[48px]" 
              value={searchQuery} 
              onChange={e => setSearchQuery(e.target.value)} 
            />
            <Button 
              type="submit" 
              className="bg-[#2C6E49] hover:bg-[#4C956C] h-12 md:h-14 px-6 md:px-8 rounded-xl md:rounded-[24px] transition-all ease-in-out duration-300 transform hover:scale-[1.02] active:scale-95 shadow-lg shadow-[#2C6E49]/20 flex items-center gap-2 w-full sm:w-auto justify-center touch-manipulation min-h-[48px]"
            >
              <Search className="h-4 md:h-5 w-4 md:w-5" />
              <span>Suchen</span>
            </Button>
          </motion.form>
          
          {/* Popular searches with better mobile touch targets */}
          <motion.div variants={item} className="mt-5 md:mt-8 flex flex-wrap justify-center gap-2 md:gap-3 text-xs md:text-sm text-[#F8F8F8]/60 px-1">
            <span className="my-1">Beliebte Suchen:</span>
            {['Friseur', 'Massage', 'Nägel', 'Coaching'].map((term, index) => (
              <React.Fragment key={term}>
                <button 
                  onClick={() => {
                    setSearchQuery(term);
                    navigate(`/booking?search=${term}`);
                  }} 
                  className="hover:text-[#4C956C] hover:scale-105 transition-all duration-200 relative py-2 px-3 rounded-full touch-manipulation active:bg-[#4C956C]/10 min-h-[44px] min-w-[44px]"
                >
                  {term}
                </button>
                {index < 3 && <span className="text-[#F8F8F8]/30 my-1">•</span>}
              </React.Fragment>
            ))}
          </motion.div>
          
          {/* Main CTA Button with optimized mobile touchpoint */}
          <motion.div variants={item} className="mt-6 md:mt-12">
            <div className="relative inline-block">
              <Button 
                onClick={() => navigate('/booking')} 
                className="bg-[#2C6E49] hover:bg-[#4C956C] px-6 md:px-12 h-auto text-base md:text-lg font-medium rounded-xl md:rounded-[24px] transition-all duration-300 hover:scale-[1.02] active:scale-95 shadow-xl shadow-[#2C6E49]/20 py-4 md:py-6 group touch-manipulation w-full sm:w-auto min-h-[56px]"
              >
                Jetzt Termin sichern
                <ArrowRight className="ml-2 h-4 md:h-5 w-4 md:w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              <div className="absolute -top-2 -right-2 bg-[#F8F8F8] text-[#212121] text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
                <Sparkles className="h-3 w-3" />
                <span>NEU</span>
              </div>
            </div>
          </motion.div>
          
          {/* Trust badge with improved contrast - better alignment on mobile */}
          <motion.div variants={item} className="mt-8 md:mt-10 inline-flex items-center gap-2 text-xs md:text-sm text-[#F8F8F8]/80 px-3 py-1.5 rounded-full bg-[#191919]/60 border border-[#F8F8F8]/10">
            <svg className="w-3.5 md:w-4 h-3.5 md:h-4 text-[#2C6E49]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
            </svg>
            <span>Über 1.000+ erfolgreiche Buchungen</span>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Subtle scroll indicator - only on desktop */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden md:flex flex-col items-center text-[#F8F8F8]/60" 
        initial={{opacity: 0, y: -10}} 
        animate={isVisible ? {opacity: 1, y: 0} : {opacity: 0, y: -10}} 
        transition={{delay: 1.2, duration: 0.5}}
      >
        <span className="text-xs mb-2">Mehr entdecken</span>
        <div className="w-5 h-9 rounded-full border border-[#F8F8F8]/20 flex justify-center pt-1">
          <motion.div 
            className="w-1 h-2 bg-[#F8F8F8]/40 rounded-full" 
            animate={{
              y: [0, 4, 0],
              opacity: [0.6, 1, 0.6]
            }} 
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: "loop"
            }} 
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
